import { useEffect, useRef } from "react"

import { useQueryClient } from "@tanstack/react-query"

import { isLiveAuthorizedFilthContentUrl } from "@/api/client/citations"
import {
  FILE_CONTENT_GC_TIME_MS,
  fileContentQueryKey,
  finishedTurnCacheScope,
  finishedTurnRetry,
  finishedTurnRetryDelay,
  useFileContent,
} from "@/hooks/use-file-content"

/**
 * Streaming-phase `cacheScope` for a file referenced by a chat turn. Keyed by
 * turn so that two turns referencing the same session filename (Code
 * Interpreter reuses names like `chart.png`) never read each other's bytes
 * while streaming; the finished-turn scope is `finishedTurnCacheScope`.
 */
export function streamingTurnCacheScope(turnId: string): string {
  return `turn-streaming:${turnId}`
}

export type TurnScopedFileContentOptions = {
  /**
   * `true` while the turn streams, `false` once it has finished, `undefined`
   * outside a chat turn (no turn policy at all).
   */
  isStreaming?: boolean
  /** Message id, or a stable slot key while the id is unknown. */
  turnId?: string
  /**
   * The stable slot key the turn used before its message id arrived. The chat
   * page keys a message by that slot until the id exists, then by the id, so
   * the id's arrival remounts the image components: this mount has loaded
   * nothing yet, and only the slot's entries hold the bytes.
   */
  slotTurnId?: string
}

type LoadedScope = { url: string; scope: string | undefined }

/**
 * `useFileContent` for a session file referenced by a chat turn, with the
 * cache policy both the generated-image card and the inline answer image
 * share (#3442, #3474):
 *
 * - While the turn streams, retries are kept (the file may not be registered
 *   yet) and the query is scoped to the turn, so a later turn reusing the
 *   filename fetches its own bytes.
 * - Once the turn has finished, one fresh attempt runs under the turn's
 *   finished scope (a 404 is asked again once, after a delay, since the
 *   durable end-of-turn registration can land just after completion) and a
 *   confirmed 404 stays final for that turn.
 * - In both phases, bytes this same mount already holds are kept when the
 *   turn id changes (the message id replacing the provisional slot key), the
 *   slot key's own entries count when `slotTurnId` names it (the page remounts
 *   the component on that transition), and after the turn has finished the
 *   turn's own streaming entry counts too. Never another turn's entry, and
 *   never a key switch that would drop bytes already held, flash a placeholder
 *   and fetch again. Bytes held under another scope are mirrored onto the
 *   finished key (same buffer, no copy) so a later remount finds them without
 *   refetching.
 * - Without a turn id the plain unscoped query and the streaming policy apply.
 *
 * Returns `data`, `isError` and the `cacheScope` in use (diagnostic, used by
 * tests).
 */
export function useTurnScopedFileContent(
  url: string | undefined,
  enabled: boolean,
  { isStreaming, turnId, slotTurnId }: TurnScopedFileContentOptions
) {
  const queryClient = useQueryClient()
  const finishedTurnId = isStreaming === false && turnId ? turnId : undefined
  const streamingTurnId = isStreaming === true && turnId ? turnId : undefined
  const lastLoadedScopeRef = useRef<LoadedScope | null>(null)

  let cacheScope: string | undefined
  if (finishedTurnId !== undefined) {
    cacheScope = finishedTurnCacheScope(finishedTurnId)
  } else if (streamingTurnId !== undefined) {
    cacheScope = streamingTurnCacheScope(streamingTurnId)
  }
  // Live-authorised FILTH content is keyed per principal and per viewer by
  // useFileContent itself; never probe or mirror those bodies across keys.
  const probeable = !!url && !isLiveAuthorizedFilthContentUrl(url)
  if (url && probeable && (finishedTurnId !== undefined || streamingTurnId !== undefined)) {
    // The scope this mount already loaded under comes first, so the key stays
    // put for the life of the mount even once other entries hold the bytes.
    const candidates: (string | undefined)[] = []
    if (lastLoadedScopeRef.current?.url === url) {
      candidates.push(lastLoadedScopeRef.current.scope)
    }
    candidates.push(cacheScope)
    if (finishedTurnId !== undefined) {
      candidates.push(streamingTurnCacheScope(finishedTurnId))
    }
    // Entries loaded under the slot key before the message id arrived (the
    // page remounts the component on that transition, see slotTurnId).
    if (slotTurnId && slotTurnId !== turnId) {
      candidates.push(finishedTurnCacheScope(slotTurnId), streamingTurnCacheScope(slotTurnId))
    }
    for (const scope of candidates) {
      if (queryClient.getQueryData(fileContentQueryKey(url, scope)) !== undefined) {
        cacheScope = scope
        break
      }
    }
  }

  const { data, isError } = useFileContent(url, enabled, {
    retry: finishedTurnId !== undefined ? finishedTurnRetry : 3,
    retryOnMount: finishedTurnId !== undefined ? "unless-not-found" : true,
    ...(finishedTurnId !== undefined ? { retryDelay: finishedTurnRetryDelay } : {}),
    cacheScope,
  })

  useEffect(() => {
    if (data === undefined || !url || !probeable) return
    lastLoadedScopeRef.current = { url, scope: cacheScope }
    // Make the finished key canonical: a remount after the turn has ended
    // probes that key first and must not refetch bytes held elsewhere.
    if (finishedTurnId !== undefined) {
      const finishedScope = finishedTurnCacheScope(finishedTurnId)
      if (cacheScope !== finishedScope) {
        const canonicalKey = fileContentQueryKey(url, finishedScope)
        if (queryClient.getQueryData(canonicalKey) === undefined) {
          // The mirror has no observer; build it with the file hook's
          // retention (a client-wide default would be scanned on every query
          // in the app and never removed).
          queryClient
            .getQueryCache()
            .build(queryClient, { queryKey: canonicalKey, gcTime: FILE_CONTENT_GC_TIME_MS })
            .setData(data, { manual: true })
        }
      }
    }
  }, [data, url, cacheScope, finishedTurnId, queryClient, probeable])

  return { data, isError, cacheScope }
}
